import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CacheProvider } from './contexts/CacheContext';
import { Toaster } from './components/ui/toaster';
import AppLoader from './components/AppLoader';
import { Analytics } from '@vercel/analytics/react';
import './index.css';

// Pages
import Dashboard from './pages/Dashboard';
import Expenses from './pages/Expenses.jsx';
import Login from './pages/Login';
import Register from './pages/Register';
import Landing from './pages/Landing';
import NotFound from './pages/NotFound';
import Investments from './pages/Investments.jsx';
import Portfolio from './pages/Portfolio.jsx';
import News from './pages/News.jsx';
import Settings from './pages/Settings';
import Reminders from './pages/Reminders';
import Loans from './pages/Loans';
import MutualFunds from './pages/MutualFunds';
import AITools from './pages/AITools';
import Consultants from './pages/Consultants';
import Income from './pages/Income';
import Terms from './pages/Terms';
import ExpenseHistory from './pages/ExpenseHistory';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    // Return a simple background to keep the site light during auth check
    return <div className="fixed inset-0 bg-slate-950" />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Global App Loader Wrapper
const AppContent = () => {
  const [initialLoad, setInitialLoad] = useState(() => {
    // Show loader only once per browser session
    // This ensures it's seen when first entering the website
    return !sessionStorage.getItem('appVisited');
  });

  const location = useLocation();

  // Trigger loader if we just logged in
  useEffect(() => {
    if (sessionStorage.getItem('justLoggedIn')) {
      setInitialLoad(true);
      sessionStorage.removeItem('justLoggedIn');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (initialLoad) {
      // First visit - show full 4s animation + linger at 100% for 0.6s
      const timer = setTimeout(() => {
        setInitialLoad(false);
        // Use sessionStorage so it only triggers once per tab session
        sessionStorage.setItem('appVisited', 'true');
      }, 4600);
      return () => clearTimeout(timer);
    }
  }, [initialLoad]);

  // Prevent page unload/reload on tab switching
  useEffect(() => {
    console.log('[FinAssure] AppContent mounted - preventing tab-switch reloads');

    const preventReload = (e) => {
      // Don't actually prevent, just ensure state is saved
      // This helps maintain state across visibility changes
      console.log('[FinAssure] beforeunload event fired - NOT reloading');
    };

    // Prevent visibility-based reloads
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[FinAssure] Tab hidden - state preserved');
      } else {
        console.log('[FinAssure] Tab visible again - NOT reloading, state preserved');
        // Tab became visible - ensure we don't reload
        // Just return without doing anything
      }
    };

    window.addEventListener('beforeunload', preventReload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      console.log('[FinAssure] AppContent unmounting - cleaning up event listeners');
      window.removeEventListener('beforeunload', preventReload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (initialLoad) {
    return <AppLoader text="INITIALIZING FINASSURE..." />;
  }

  return (
    <div className="bg-slate-950 min-h-screen font-sans text-gray-100">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />

        {/* Protected Routes */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute><Expenses /></ProtectedRoute>} />
        <Route path="/income" element={<ProtectedRoute><Income /></ProtectedRoute>} />
        <Route path="/investments" element={<ProtectedRoute><Investments /></ProtectedRoute>} />
        <Route path="/investments/mutual-funds" element={<ProtectedRoute><MutualFunds /></ProtectedRoute>} />
        <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
        <Route path="/loans" element={<ProtectedRoute><Loans /></ProtectedRoute>} />
        <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
        <Route path="/reminders" element={<ProtectedRoute><Reminders /></ProtectedRoute>} />
        <Route path="/ai-tools" element={<ProtectedRoute><AITools /></ProtectedRoute>} />
        <Route path="/consultants" element={<ProtectedRoute><Consultants /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/expense-history" element={<ProtectedRoute><ExpenseHistory /></ProtectedRoute>} />
        <Route path="/terms" element={<Terms />} />

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <Toaster />
    </div>
  );
}

function App() {
  return (
    <Router>
      <CacheProvider>
        <AuthProvider>
          <AppContent />
          <Analytics />
        </AuthProvider>
      </CacheProvider>
    </Router>
  );
}

export default App;
